import {AlignmentSummary} from 'constants/AlignmentConstants';
import {StringIDFolder} from 'constants/FolderConstants';
import ModelTypeConstants, {SEQUENCE_SUBTYPES} from 'constants/ModelTypeConstants';
import {StorableSummary} from 'constants/SampleConstants';
import {BioentitySummary} from 'folder-items/FileConstants';
import {EntrySummary, ProtocolSummary} from 'notebook/ELNConstants';
import {Request} from 'requests/RequestConstants';
import {SidepanelRun} from 'runs/sidepanel/RunsSidepanelConstants';
import {ListableGroup, ListableItemExtras} from 'search/SearchConstants';
import {ScrollableTableColumn} from 'ui-library/scrollable/ScrollableTableConstants';
import {BasicEvent} from 'util/Types';

import {AnalysisSearchAttributesGraphQL} from '../../../graphql-fragments/analysis-tool/__generated/AnalysisSearchAttributesGraphQL';
import {JellyfishListingGraphQL} from '../../../graphql-fragments/app-core/archetype/__generated/JellyfishListingGraphQL';
import {DatasetListingGraphQL} from '../../../graphql-fragments/data-frames/__generated/DatasetListingGraphQL';
import {FileListingGraphQL} from '../../../graphql-fragments/files/__generated/FileListingGraphQL';
import {AnalyticsDashboardSearchAttributesGraphQL} from '../../../graphql-fragments/insights/__generated/AnalyticsDashboardSearchAttributesGraphQL';
import {ConnectionListingGraphQL} from '../../../graphql-fragments/instruments/__generated/ConnectionListingGraphQL';
import {BulkAssemblyListingGraphQL} from '../../../graphql-fragments/molbio/__generated/BulkAssemblyListingGraphQL';
import {CollectibleListingGraphQL} from '../../../graphql-fragments/notebook/templates/__generated/CollectibleListingGraphQL';
import {TemplateCollectionListingGraphQL} from '../../../graphql-fragments/notebook/templates/__generated/TemplateCollectionListingGraphQL';
import {ProcessListingGraphQL} from '../../../graphql-fragments/processes/__generated/ProcessListingGraphQL';
import {ProcessOutputListingGraphQL} from '../../../graphql-fragments/processes/__generated/ProcessOutputListingGraphQL';
import {ProcessTaskListingGraphQL} from '../../../graphql-fragments/processes/__generated/ProcessTaskListingGraphQL';
import {RequestV2DefinitionListingGraphQL} from '../../../graphql-fragments/processes/requests-v2/__generated/RequestV2DefinitionListingGraphQL';
import {RequestV2SubmissionListingGraphQL} from '../../../graphql-fragments/processes/requests-v2/__generated/RequestV2SubmissionListingGraphQL';
import {TestDefinitionListingGraphQL} from '../../../graphql-fragments/sample_tests/__generated/TestDefinitionListingGraphQL';
import {TestOrderListingGraphQL} from '../../../graphql-fragments/sample_tests/__generated/TestOrderListingGraphQL';
import {StudyListingGraphQL} from '../../../graphql-fragments/studies/__generated/StudyListingGraphQL';
import {StageEntrySummaryGraphQL} from '../../../graphql-fragments/workflows/__generated/StageEntrySummaryGraphQL';
import {WorkflowSummaryGraphQL} from '../../../graphql-fragments/workflows/__generated/WorkflowSummaryGraphQL';
import {WorklistSummaryGraphQL} from '../../../graphql-fragments/worklists/__generated/WorklistSummaryGraphQL';
import {WorksheetListingGraphQL} from '../../../graphql-fragments/worksheets/__generated/WorksheetListingGraphQL';

// Keep in sync with getListingRendererTypeFromModelType, which is used to get sorts from a TypeFilter value
export enum ListingRendererType {
  ALIGNMENT = 'ALIGNMENT',
  ANALYSIS = 'ANALYSIS',
  ANALYTICS_DASHBOARD = 'ANALYTICS_DASHBOARD',
  // Assay task types
  ASSAY_RUN_TASK = 'ASSAY_RUN_TASK',
  ASSAY_STEP_TASK = 'ASSAY_STEP_TASK',
  ASSAY_UNIT_OPERATION_TASK = 'ASSAY_UNIT_OPERATION_TASK',
  COLLECTIBLE = 'COLLECTIBLE',
  COLLECTION = 'COLLECTION',
  CONNECTION = 'CONNECTION',
  FOLDER_ITEM = 'FOLDER_ITEM',
  GROUP = 'GROUP',
  JELLYFISH = 'JELLYFISH',
  // Workflows types
  PROCESS = 'PROCESS',
  PROCESS_OUTPUT = 'PROCESS_OUTPUT',
  PROCESS_TASK = 'PROCESS_TASK',
  // Recipe task types
  RECIPE_RUN_TASK = 'RECIPE_RUN_TASK',
  RECIPE_STEP_TASK = 'RECIPE_STEP_TASK',
  RECIPE_UNIT_OPERATION_TASK = 'RECIPE_UNIT_OPERATION_TASK',
  REQUEST = 'REQUEST',
  REQUEST_V2_DEFINITION = 'REQUEST_V2_DEFINITION',
  REQUEST_V2_SUBMISSION = 'REQUEST_V2_SUBMISSION',
  RUN = 'RUN',
  STORABLE = 'STORABLE',
  STUDY = 'STUDY',
  TEST_DEFINITION = 'TEST_DEFINITION',
  TEST_ORDER = 'TEST_ORDER',
  WORKFLOW = 'WORKFLOW',
  WORKLIST = 'WORKLIST',
  WORKSHEET = 'WORKSHEET',
}

export type WorklistItem<T> = T & {worklistItemPosition: number};

export function isWorklistItem<T>(item: T): item is WorklistItem<T> {
  return (item as WorklistItem<T>).worklistItemPosition != null;
}

const ALIGNMENT_MODEL_TYPE_TO_LISTING_RENDERER_TYPE: Partial<
  Record<ModelTypeConstants, ListingRendererType>
> = {
  [ModelTypeConstants.PROTEIN_ALIGNMENT]: ListingRendererType.ALIGNMENT,
  [ModelTypeConstants.SEQUENCE_ANALYSIS]: ListingRendererType.ALIGNMENT,
};

const COLLECTIBLE_MODEL_TYPE_TO_LISTING_RENDERER_TYPE: Partial<
  Record<ModelTypeConstants, ListingRendererType>
> = {
  [ModelTypeConstants.FORM_DEFINITION]: ListingRendererType.COLLECTIBLE,
  [ModelTypeConstants.SUBTEMPLATE]: ListingRendererType.COLLECTIBLE,
  [ModelTypeConstants.TEMPLATE]: ListingRendererType.COLLECTIBLE,
  [ModelTypeConstants.RECIPE_PROCEDURE]: ListingRendererType.COLLECTIBLE,
  [ModelTypeConstants.ASSAY_PROCEDURE]: ListingRendererType.COLLECTIBLE,
  [ModelTypeConstants.REQUEST_V2_DEFINITION]: ListingRendererType.COLLECTIBLE,
  [ModelTypeConstants.ANALYSIS_TEMPLATE]: ListingRendererType.COLLECTIBLE,
};

const FOLDER_ITEM_MODEL_TYPE_TO_LISTING_RENDERER_TYPE: Record<string, ListingRendererType> = {
  [ModelTypeConstants.BASIC_FOLDER_ITEM]: ListingRendererType.FOLDER_ITEM,
  [ModelTypeConstants.BULK_ASSEMBLY]: ListingRendererType.FOLDER_ITEM,
  [ModelTypeConstants.DATASET]: ListingRendererType.FOLDER_ITEM,
  [ModelTypeConstants._DO_NOT_USE_ANTIBODY]: ListingRendererType.FOLDER_ITEM,
  [ModelTypeConstants.EQUIPMENT]: ListingRendererType.FOLDER_ITEM,
  [ModelTypeConstants._DO_NOT_USE_OLIGO_CONJUGATE]: ListingRendererType.FOLDER_ITEM,
  [ModelTypeConstants._DO_NOT_USE_OLIGO_DUPLEX]: ListingRendererType.FOLDER_ITEM,
  [ModelTypeConstants.ENTITY]: ListingRendererType.FOLDER_ITEM,
  [ModelTypeConstants.ENTRY]: ListingRendererType.FOLDER_ITEM,
  [ModelTypeConstants.FILE]: ListingRendererType.FOLDER_ITEM,
  [ModelTypeConstants.FOLDER]: ListingRendererType.FOLDER_ITEM,
  [ModelTypeConstants.MIXTURE]: ListingRendererType.FOLDER_ITEM,
  [ModelTypeConstants.MOLECULE]: ListingRendererType.FOLDER_ITEM,
  [ModelTypeConstants.PROTEIN]: ListingRendererType.FOLDER_ITEM,
  [ModelTypeConstants.PROTOCOL]: ListingRendererType.FOLDER_ITEM,
  [ModelTypeConstants.SEQUENCE]: ListingRendererType.FOLDER_ITEM,
  [ModelTypeConstants.STAGE_ENTRY]: ListingRendererType.FOLDER_ITEM,
  // All subtypes for sequences are folder items
  ...Object.fromEntries(SEQUENCE_SUBTYPES.map((subtype) => [subtype, ListingRendererType.FOLDER_ITEM])),
};

// Recipe task types are subtypes of process tasks.
// However, when we use this record, task types should already be mapped to their subtype
const PROCESS_TASK_MODEL_TYPE_TO_LISTING_RENDERER_TYPE: Partial<
  Record<ModelTypeConstants, ListingRendererType>
> = {
  [ModelTypeConstants.PROCESS_TASK]: ListingRendererType.PROCESS_TASK,
  [ModelTypeConstants.RECIPE_RUN_TASK]: ListingRendererType.RECIPE_RUN_TASK,
  [ModelTypeConstants.RECIPE_STEP_TASK]: ListingRendererType.RECIPE_STEP_TASK,
  [ModelTypeConstants.RECIPE_UNIT_OPERATION_TASK]: ListingRendererType.RECIPE_UNIT_OPERATION_TASK,
  [ModelTypeConstants.ASSAY_RUN_TASK]: ListingRendererType.ASSAY_RUN_TASK,
  [ModelTypeConstants.ASSAY_UNIT_OPERATION_TASK]: ListingRendererType.ASSAY_UNIT_OPERATION_TASK,
  [ModelTypeConstants.ASSAY_STEP_TASK]: ListingRendererType.ASSAY_STEP_TASK,
};

const STORABLE_MODEL_TYPE_TO_LISTING_RENDERER_TYPE: Partial<Record<ModelTypeConstants, ListingRendererType>> =
  {
    [ModelTypeConstants.BOX]: ListingRendererType.STORABLE,
    [ModelTypeConstants.CONTAINER]: ListingRendererType.STORABLE,
    [ModelTypeConstants.LOCATION]: ListingRendererType.STORABLE,
    [ModelTypeConstants.PLATE]: ListingRendererType.STORABLE,
  };

const MODEL_TYPE_TO_LISTING_RENDERER_TYPE: Partial<Record<ModelTypeConstants, ListingRendererType>> = {
  ...ALIGNMENT_MODEL_TYPE_TO_LISTING_RENDERER_TYPE,
  ...COLLECTIBLE_MODEL_TYPE_TO_LISTING_RENDERER_TYPE,
  ...FOLDER_ITEM_MODEL_TYPE_TO_LISTING_RENDERER_TYPE,
  ...PROCESS_TASK_MODEL_TYPE_TO_LISTING_RENDERER_TYPE,
  ...STORABLE_MODEL_TYPE_TO_LISTING_RENDERER_TYPE,
  [ModelTypeConstants.ANALYSIS]: ListingRendererType.ANALYSIS,
  [ModelTypeConstants.ANALYTICS_DASHBOARD]: ListingRendererType.ANALYTICS_DASHBOARD,
  [ModelTypeConstants.ASSAY_PROCESS]: ListingRendererType.PROCESS,
  [ModelTypeConstants.ASSAY_RUN]: ListingRendererType.RUN,
  [ModelTypeConstants.CONNECTION]: ListingRendererType.CONNECTION,
  [ModelTypeConstants.JELLYFISH]: ListingRendererType.JELLYFISH,
  [ModelTypeConstants.PROCESS]: ListingRendererType.PROCESS,
  [ModelTypeConstants.PROCESS_OUTPUT]: ListingRendererType.PROCESS_OUTPUT,
  [ModelTypeConstants.RECIPE_PROCESS]: ListingRendererType.PROCESS,
  [ModelTypeConstants.REQUEST]: ListingRendererType.REQUEST,
  [ModelTypeConstants.REQUEST_V2_DEFINITION]: ListingRendererType.REQUEST_V2_DEFINITION,
  [ModelTypeConstants.REQUEST_V2_SUBMISSION]: ListingRendererType.REQUEST_V2_SUBMISSION,
  [ModelTypeConstants.STUDY]: ListingRendererType.STUDY,
  [ModelTypeConstants.TEMPLATE_COLLECTION]: ListingRendererType.COLLECTION,
  [ModelTypeConstants.TEST_DEFINITION]: ListingRendererType.TEST_DEFINITION,
  [ModelTypeConstants.TEST_ORDER]: ListingRendererType.TEST_ORDER,
  [ModelTypeConstants.WORKFLOW]: ListingRendererType.WORKFLOW,
  [ModelTypeConstants.WORKLIST]: ListingRendererType.WORKLIST,
  [ModelTypeConstants.WORKSHEET]: ListingRendererType.WORKSHEET,
};

export function getListingRendererTypeFromModelType(modelType: string): ListingRendererType | null {
  return MODEL_TYPE_TO_LISTING_RENDERER_TYPE[modelType as ModelTypeConstants] ?? null;
}

export function getModelTypesForListingRendererType(type: ListingRendererType): Array<ModelTypeConstants> {
  return Object.entries(MODEL_TYPE_TO_LISTING_RENDERER_TYPE)
    .filter(([modelType, rendererType]) => rendererType === type)
    .map(([modelType, rendererType]) => modelType as ModelTypeConstants);
}

export interface ListingTypes {
  [ListingRendererType.ALIGNMENT]: AlignmentSummary;
  [ListingRendererType.ANALYSIS]: AnalysisSearchAttributesGraphQL;
  [ListingRendererType.ANALYTICS_DASHBOARD]: AnalyticsDashboardSearchAttributesGraphQL;
  [ListingRendererType.ASSAY_RUN_TASK]: ProcessTaskListingGraphQL;
  [ListingRendererType.ASSAY_STEP_TASK]: ProcessTaskListingGraphQL;
  [ListingRendererType.ASSAY_UNIT_OPERATION_TASK]: ProcessTaskListingGraphQL;
  [ListingRendererType.COLLECTIBLE]: CollectibleListingGraphQL;
  [ListingRendererType.COLLECTION]: TemplateCollectionListingGraphQL;
  [ListingRendererType.CONNECTION]: ConnectionListingGraphQL;
  [ListingRendererType.FOLDER_ITEM]:
    | StringIDFolder
    | (BioentitySummary | WorklistItem<BioentitySummary>)
    | EntrySummary
    | ProtocolSummary
    | StageEntrySummaryGraphQL
    | ProcessListingGraphQL
    | BulkAssemblyListingGraphQL
    | WorksheetListingGraphQL
    | DatasetListingGraphQL
    | FileListingGraphQL;
  [ListingRendererType.GROUP]: ListableGroup;
  [ListingRendererType.JELLYFISH]: JellyfishListingGraphQL;
  [ListingRendererType.PROCESS]: ProcessListingGraphQL;
  [ListingRendererType.PROCESS_OUTPUT]: ProcessOutputListingGraphQL;
  [ListingRendererType.PROCESS_TASK]: ProcessTaskListingGraphQL;
  [ListingRendererType.RECIPE_RUN_TASK]: ProcessTaskListingGraphQL;
  [ListingRendererType.RECIPE_STEP_TASK]: ProcessTaskListingGraphQL;
  [ListingRendererType.RECIPE_UNIT_OPERATION_TASK]: ProcessTaskListingGraphQL;
  [ListingRendererType.REQUEST]: Request;
  [ListingRendererType.REQUEST_V2_DEFINITION]: RequestV2DefinitionListingGraphQL;
  [ListingRendererType.REQUEST_V2_SUBMISSION]: RequestV2SubmissionListingGraphQL;
  [ListingRendererType.RUN]: SidepanelRun;
  [ListingRendererType.STORABLE]: StorableSummary | WorklistItem<StorableSummary>;
  [ListingRendererType.STUDY]: StudyListingGraphQL;
  [ListingRendererType.TEST_DEFINITION]: TestDefinitionListingGraphQL;
  [ListingRendererType.TEST_ORDER]: TestOrderListingGraphQL;
  [ListingRendererType.WORKFLOW]: WorkflowSummaryGraphQL;
  [ListingRendererType.WORKLIST]: WorklistSummaryGraphQL;
  [ListingRendererType.WORKSHEET]: WorksheetListingGraphQL;
}

export enum ColumnAccessorKey {
  ID = 'id',
  NAME = 'name',

  ALIASES = 'otherAliases',
  ANALYSIS_COMMENT = 'analysisComment',
  ASSIGNEE = 'assignee',
  ASSIGNEES = 'assignees',
  AUTHORS = 'authors',
  BIT_SCORE = 'bitScore',
  CHEMICAL_SIMILARITY = 'chemicalSimilarity',
  CHEMICAL_STRUCTURE = 'chemical_structure',
  COLLECTION = 'collection',
  COLLECTIBLE_TYPE = 'collectible_type',
  COLLECTION_COUNTS = 'counts',
  CONCENTRATION = 'concentration',
  CONNECTION_GATEWAY = 'connectionGateway',
  CONNECTION_STATUS = 'connectionStatus',
  CONNECTION_EQUIPMENT = 'connectionEquipment',
  CONTAINED_ITEMS = 'containedItems',
  CONTAINER = 'container',
  CREATED_AT = 'createdAt',
  CREATOR = 'creator',
  DESCRIPTION = 'description',
  DILUTION_FACTOR = 'dilutionFactor',
  ENTRY_DATES = 'entryDates',
  EXECUTION_ENTRY = 'executionEntry',
  EXECUTION_TYPE = 'executionType',
  EXPERIMENTAL_ROLE = 'experimentalRole',
  EXPIRATION_DATE = 'expirationDate',
  E_VALUE = 'eValue',
  FILE = 'file',
  FREEZE_THAW_COUNT = 'freezeThawCount',
  GC_PERCENTAGE = 'gc',
  GRID_DIMENSIONS = 'gridDimensions',
  HIT_LENGTH = 'hitLength',
  HIT_RANGE = 'hitRange',
  INVENTORY = 'inventory',
  LAST_SEEN = 'lastSeen',
  LAST_STATUS_CHANGE = 'lastStatusChange',
  MELTING_TEMPERATURE = 'tm',
  MIXTURE_AMOUNT = 'mixtureAmount',
  MIXTURE_INGREDIENTS = 'mixtureIngredients',
  MODIFIED_AT = 'date',
  OLIGO_MAP = 'oligoMap',
  ORIGIN_ENTRY = 'originEntry',
  OWNER = 'owner',
  PARENT_STORABLE = 'parentStorable',
  PERCENT_IDENTITY = 'percentIdentity',
  PHASE = 'phase',
  PLATE_TYPE = 'plateType',
  POSITION = 'position',
  PROCESS_ID = 'processId',
  PROCESS_NAME = 'processName',
  PROCESS_TASK_ID = 'processTaskId',
  PROCESS_TASK_TYPE = 'processTaskType',
  ROOT_PROCESS_TASK = 'rootProcessTask',
  ROOT_PROCESS = 'rootProcess',
  PROJECT = 'project',
  PUBLISHING_STATUS = 'publishingStatus',
  QUERY_COVER = 'queryCover',
  ASSAY_CONDITION_REPLICATE_RUN = 'assayConditionReplicateRun',
  ASSAY_UNIT_OPERATION = 'assayUnitOperation',
  ASSAY_CONDITIONS = 'assayConditions',
  ASSAY_REPLICATE = 'assayReplicate',
  ASSAY_TEMPLATE = 'assayTemplate',
  RECIPE_TEMPLATE = 'recipeTeemplate',
  RECIPE_CONDITION_REPLICATE_RUN = 'recipeConditionReplicateRun',
  RECIPE_UNIT_OPERATION = 'recipeUnitOperation',
  RECIPE_CONDITIONS = 'recipeConditions',
  RECIPE_REPLICATE = 'recipeReplicate',
  REGISTRY_IDENTIFIER = 'registryIdentifier',
  REQUESTED_TASK_SCHEMAS = 'requestedTaskSchemas',
  REQUESTS_REQUESTOR = 'requestor',
  RESIDUES = 'residues',
  RESTRICTED_SAMPLE_USERS = 'restrictedSampleUsers',
  REVIEW_LIFECYCLE = 'reviewLifecycle',
  REVIEW_LIFECYCLES = 'reviewLifecycles',
  REVIEW_LIFECYCLE_TYPE = 'reviewLifecycleType',
  REVIEW_TIMESTAMP_FIRST_STARTED = 'reviewTimestampFirstStarted',
  REVIEW_TIMESTAMP_LAST_ACCEPTED = 'reviewTimestampLastAccepted',
  REVIEW_TIMESTAMP_LAST_STARTED = 'reviewTimestampLastStarted',
  REQUEST_V2_SUBMISSION = 'requestV2Submission',
  SAMPLE = 'sample',
  SAMPLE_DEFINITIONS = 'sampleDefinitions',
  SAMPLE_OWNERS = 'sampleOwners',
  SAMPLE_RESTRICTION_ACCESS = 'sampleRestrictionAccess',
  SAMPLE_RESTRICTION_STATUS = 'sampleRestrictionStatus',
  SAMPLE_STATUS_COUNTS = 'sampleStatusCounts',
  SCHEDULED_ON = 'scheduledOn',
  SCHEMA = 'schema',
  SEQUENCE = 'sequence',
  STAR = 'star',
  STATUS = 'status',
  STORABLE_SCHEMA = 'storableSchema',
  STUDIES = 'studies',
  TASK_RESPONSIBLE_PARTIES = 'taskResponsibleParties',
  TASK_STATUS = 'taskStatus',
  TEST_DEFINITION = 'testDefinition',
  TOTAL_CAPACITY = 'totalCapacity',
  TYPE = 'type',
  VOLUME = 'volume',
  WATCHERS = 'watchers',
  WORKSHEET = 'worksheet',
}

export interface ColumnConfiguration<K> {
  column: ScrollableTableColumn;
  render: (item: K & Partial<ListableItemExtras>) => React.ReactNode;
  isHidden?: (item: K) => boolean;
}

export interface ListingRenderer<T extends ListingRendererType> {
  getColumnConfigurations(
    items: Array<ListingTypes[T]>,
    extras: unknown
  ): Array<ColumnConfiguration<ListingTypes[T]>>;

  getRowContentHeight(item: ListingTypes[T], extras: unknown): number;

  renderIcon(item: ListingTypes[T], options: unknown): JSX.Element;

  openContextMenu(item: ListingTypes[T], e: BasicEvent, options: unknown): void;

  onClick?(item: ListingTypes[T], openItem: (item: ListingTypes[T]) => void): void;
}
